import { useMemo } from 'react';
import { useLocalData } from '../hooks/useLocalData';
import { LocalAnalyticsData } from '../services/localDataService';
// Analytics currently uses local analytics data only; remove unused model import
import './Screen.css';

export default function Analytics() {
  const { data: analytics, loading } = useLocalData<LocalAnalyticsData>('analytics');

  const metrics = useMemo(() => {
    const grouped = analytics.reduce((acc, item) => {
      if (!acc[item.metric]) {
        acc[item.metric] = [];
      }
      acc[item.metric].push(item);
      return acc;
    }, {} as Record<string, LocalAnalyticsData[]>);

    return Object.entries(grouped).map(([metric, values]) => ({
      metric,
      values,
      average: values.reduce((sum, v) => sum + (v.value || 0), 0) / values.length,
      total: values.reduce((sum, v) => sum + (v.value || 0), 0),
      latest: values[values.length - 1]?.value || 0,
    }));
  }, [analytics]);

  const categories = useMemo(() => {
    const grouped = analytics.reduce((acc, item) => {
      const category = item.category || 'uncategorized';
      acc[category] = (acc[category] || 0) + (item.value || 0);
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(grouped);
  }, [analytics]);

  return (
    <div className="screen">
      <div className="screen-header">
        <h1>Analytics</h1>
        <p className="screen-subtitle">View application analytics and metrics</p>
      </div>

      {loading ? (
        <div className="loading">Loading analytics...</div>
      ) : (
        <>
          <div className="analytics-grid">
            {metrics.map((metric) => (
              <div key={metric.metric} className="analytics-card">
                <h3>{metric.metric}</h3>
                <div className="metric-value">{metric.latest.toLocaleString()}</div>
                <div className="metric-details">
                  <span>Average: {metric.average.toFixed(2)}</span>
                  <span>Total: {metric.total.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="content-section">
            <h2>Category Distribution</h2>
            <div className="category-chart">
              {categories.map(([category, value]) => (
                <div key={category} className="category-item">
                  <div className="category-header">
                    <span>{category}</span>
                    <span>{value.toLocaleString()}</span>
                  </div>
                  <div className="category-bar">
                    <div
                      className="category-bar-fill"
                      style={{
                        width: `${(value / Math.max(...categories.map(([, v]) => v))) * 100}%`
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="content-section">
            <h2>Recent Data Points</h2>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Value</th>
                    <th>Category</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.slice(-20).reverse().map((item) => (
                    <tr key={item.id}>
                      <td>{item.metric}</td>
                      <td>{(item.value || 0).toLocaleString()}</td>
                      <td>{item.category || ''}</td>
                      <td>{item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

